import { useId, type InputHTMLAttributes } from 'react';
import { Icon, type IconName } from './Icon';
import styles from './Input.module.css';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id'> {
  label?: string;
  error?: string;
  helpText?: string;
  icon?: IconName;
  id?: string;
}

/**
 * Port of components/forms/Input.jsx. Focus state moved from a JS
 * onFocus/onBlur handler to :focus-within so keyboard and touch input also
 * trigger the border/shadow change.
 */
export function Input({ label, error, helpText, icon, id, className, ...rest }: InputProps) {
  const generatedId = useId();
  const inputId = id ?? generatedId;
  const fieldClasses = [styles.field, error ? styles.fieldError : null].filter(Boolean).join(' ');

  return (
    <div className={[styles.wrapper, className].filter(Boolean).join(' ')}>
      {label && (
        <label htmlFor={inputId} className={styles.label}>
          {label}
        </label>
      )}
      <div className={fieldClasses}>
        {icon && <Icon name={icon} size={16} color="var(--text-tertiary)" />}
        <input id={inputId} className={styles.input} {...rest} />
      </div>
      {(error || helpText) && (
        <span className={error ? styles.errorText : styles.helpText}>{error || helpText}</span>
      )}
    </div>
  );
}
